import { FC } from 'react';

import { RefreshCw } from 'lucide-react';

import { ProductCardsGrid } from './ProductCardsGrid';
import { InfiniteScrollTrigger } from '../../components/shared/InfiniteScrollTrigger';
import { Button } from '../../components/ui/Button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/Select';
import { CartonProduct, ProductType } from '../../types';
import { getProductsCountText } from '../../utils';

interface Props {
    readonly areProductsEmpty: boolean;
    readonly products: CartonProduct[];
    readonly nextPage: number | null;
    readonly loading: boolean;
    readonly loadingMore: boolean;
    readonly loadMore: () => void;
    readonly resetFilters: () => void;
    readonly sort: string;
    readonly onSortChange: (value: string) => void;
    readonly currentProductType?: ProductType | null;
    readonly heading?: string;
    readonly resultsCount?: number;
    readonly subtitle?: string;
    readonly showUniverseBadge?: boolean;
    readonly emptyTitle?: string;
    readonly emptyDescription?: string;
    readonly showResetButton?: boolean;
    readonly withDesktopSidebar?: boolean;
}

export const ProductsGrid: FC<Props> = ({
    areProductsEmpty,
    products,
    nextPage,
    loading,
    loadingMore,
    loadMore,
    resetFilters,
    sort,
    onSortChange,
    currentProductType,
    heading: headingOverride,
    resultsCount,
    subtitle,
    showUniverseBadge = false,
    emptyTitle = 'Товари не знайдено',
    emptyDescription = 'На жаль, нічого не знайдено за вашими критеріями пошуку. Спробуйте змінити фільтри або перегляньте інші категорії',
    showResetButton = true,
    withDesktopSidebar = false,
}) => {
    const heading = headingOverride || currentProductType?.name || 'Усі товари';

    if (areProductsEmpty) {
        return (
            <div className="mx-auto flex flex-col items-center justify-center px-4 py-12 text-center">
                <div className="mb-4 rounded-full bg-muted/50 p-4"></div>
                <h3 className="mb-2 text-xl font-medium">Товари не знайдено</h3>
                <p className="mb-6 max-w-md text-muted-foreground">
                    На жаль, обраний тип товарів відсутній в наявності
                </p>
            </div>
        );
    }

    return (
        <div className="flex-1">
            <div className="mb-4 flex flex-col justify-between gap-4 sm:mb-6 sm:flex-row sm:items-center">
                <div className="space-y-1">
                    <div
                        className="flex items-baseline gap-2"
                        data-testid="products-grid-heading-row"
                    >
                        <h2
                            className="text-lg font-bold sm:text-2xl md:block md:text-xl"
                            data-testid="products-grid-heading"
                        >
                            {heading}
                        </h2>
                        {typeof resultsCount === 'number' && (
                            <span
                                className="text-sm font-normal whitespace-nowrap text-muted-foreground"
                                data-testid="products-grid-results-count"
                            >
                                {getProductsCountText(resultsCount)}
                            </span>
                        )}
                    </div>

                    {subtitle && (
                        <p className="text-sm text-muted-foreground">
                            {subtitle}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm whitespace-nowrap text-muted-foreground">
                        Сортувати за:
                    </span>
                    <Select
                        defaultValue={sort || 'newest'}
                        onValueChange={onSortChange}
                    >
                        <SelectTrigger className="h-9 w-full rounded-xl text-sm sm:w-[160px] [&_svg]:h-3.5 [&_svg]:w-3.5">
                            <SelectValue placeholder="Новизною" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl text-sm">
                            <SelectItem className="py-1 text-sm" value="newest">
                                Новизною
                            </SelectItem>
                            <SelectItem
                                className="py-1 text-sm"
                                value="price-low"
                            >
                                Ціна: від низької до високої
                            </SelectItem>
                            <SelectItem
                                className="py-1 text-sm"
                                value="price-high"
                            >
                                Ціна: від високої до низької
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {products?.length > 0 ? (
                <ProductCardsGrid
                    products={products}
                    loading={loading}
                    showUniverseBadge={showUniverseBadge}
                    className={
                        withDesktopSidebar
                            ? 'lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5'
                            : undefined
                    }
                />
            ) : (
                <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                    <div className="mb-4 rounded-full bg-muted/50 p-4"></div>
                    <h3 className="mb-2 text-xl font-medium">{emptyTitle}</h3>
                    <p className="mb-6 max-w-md text-muted-foreground">
                        {emptyDescription}
                    </p>
                    {showResetButton && (
                        <Button
                            variant="outline"
                            className="gap-2"
                            onClick={resetFilters}
                        >
                            <RefreshCw className="h-4 w-4" />
                            Скинути фільтри
                        </Button>
                    )}
                </div>
            )}

            {nextPage && (
                <InfiniteScrollTrigger
                    className="mt-8"
                    loading={loading || loadingMore}
                    onLoadMore={loadMore}
                />
            )}
        </div>
    );
};
